import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Breadcrumb, Form, Spinner } from 'react-bootstrap';
import { Link, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../contexts/auth.context';
import {
  bulkWaitlistEntryState,
  closeWaitlistWave,
  getWaitlistAudit,
  getWaitlistCodes,
  getWaitlistEntries,
  getWaitlistEntriesCsv,
  getWaitlistAntisybilPage,
  getWaitlistPrizeItems,
  listWaitlistPromos,
  getWaitlistSettings,
  getWaitlistSettingsWithLastWave,
  getWaitlistWaveReport,
  mintWaitlistCodes,
  saveWaitlistSettings,
  setWaitlistEntryState,
  setWaitlistLastWave,
  grantWaitlistJelly,
} from '../../apis/waitlist.api';
import { WavesList, WaveWorkspace } from './WavesPanel';
import { formatUtcWindow } from '../../utils/utcDateTime';
import { wavesFromAudit } from './ladderShape';
import {
  NO_WAITLIST_COPY,
  PROMO_LIST_FAILED_COPY,
  isNoWaitlistError,
  loadFailureCopy,
} from './loadFailure';
import { wavePath, withWaitlist } from './wavePath';
import { canFreezeWaitlistEntries, isWaitlistMintOnlyWallet } from '../../utils/constants';

// The Waitlist tab (TRU-1066/1079), SAMPLE A (Gwen's pick, 2026-08-19):
// the tab is ONLY a list of waves. Click a wave for its detail — a Wave
// info card, then tabs: Invite Codes (create + usage progress) / Code
// Entries / Audit / Report (closed waves). Creating a wave is a popup
// (Create Promo's datetime pickers) and the new wave loads on create.
// The tab rides the URL, so the breadcrumb always tells the truth:
// Waves > wave-2 > Invite Codes. Backed by waitlist-svc's admin routes —
// dev runs against the local mock (REACT_APP_WAITLIST_URL).
const TAB_NAMES = {
  report: 'Report',
  codes: 'Invite Codes',
  entries: 'Code Entries',
  audit: 'Audit',
};

const pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

export const loadCompletedWaveReport = async (
  load,
  waveId,
  { attempts = 20, delay = pause } = {}
) => {
  let lastError = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const answer = await load();
      if (answer?.report?.waveId === waveId && Array.isArray(answer?.rows)) return answer;
    } catch (error) {
      lastError = error;
    }
    if (attempt + 1 < attempts) await delay(1000);
  }
  throw lastError ?? new Error(`Wave ${waveId} report did not finish in time.`);
};

const WaveRoute = props => {
  const { waveId, tab } = useParams();
  return (
    <WaveWorkspace
      waveId={waveId.toLowerCase()}
      tab={tab && TAB_NAMES[tab] ? tab : undefined}
      {...props}
    />
  );
};

const Waitlist = () => {
  const [stageEmpty, setStageEmpty] = useState(false);
  const [codes, setCodes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);
  // Promo-binding v2: the picker. Options are atlantis WAITLIST promos;
  // the selection scopes the whole page (waves, prize items, codes). The
  // default follows the latest seal in the settings response, then the
  // env fallback, then the newest promo.
  const [waitlists, setWaitlists] = useState([]);
  const [waitlistId, setWaitlistId] = useState(null);
  const waitlistIdRef = useRef(null);
  const [prizeItems, setPrizeItems] = useState([]);
  const [prizeItemsError, setPrizeItemsError] = useState(null);
  const [promoWindow, setPromoWindow] = useState(null);
  const [waveReport, setWaveReport] = useState(null);
  const [waveBusy, setWaveBusy] = useState(false);
  const [lastWaveId, setLastWaveId] = useState(null);
  const [lastWaveBusy, setLastWaveBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const reportRequest = useRef(0);
  const dataRequest = useRef(0);
  const loadingRequest = useRef(0);

  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);
  // Username is the signed-in wallet. Waitlist mint-only operators (Owen,
  // Holy, Spectator) keep Invite Codes. Close + Grant, Grant Jelly,
  // edit/create wave, and exports stay off this client. Holy and Spectator
  // can still freeze. Owen cannot. waitlist-svc still enforces the admin
  // ACL — these flags do not grant or bypass it.
  const mintOnly = isWaitlistMintOnlyWallet(auth?.username);
  const canFreeze = canFreezeWaitlistEntries(auth?.username);

  // A shared wave link names its waitlist (?waitlist=<promoId>): the first
  // load honours it, so the linked wave is there instead of "missing" under
  // the service default.
  const linkedWaitlist = new URLSearchParams(location.search).get('waitlist') || null;

  useEffect(() => {
    authToken.current = auth?.idToken;
    fetchAll(false, waitlistId ?? linkedWaitlist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.idToken]);

  // quiet = refresh the data WITHOUT the global loading flip — a modal
  // showing its result must stay mounted through the refetch (the report
  // tab already paid for this lesson once).
  const fetchAll = async (quiet = false, scopeId = waitlistId) => {
    const requestId = ++dataRequest.current;
    if (!quiet) {
      loadingRequest.current = requestId;
      setLoading(true);
    }
    setError(null);
    setStageEmpty(false);
    try {
      const { promos, failed: promosFailed } = await listWaitlistPromos(authToken.current);
      if (requestId !== dataRequest.current) return false;
      setWaitlists(promos);
      let probeEmpty = false;
      let [codeRows, entryRows, auditRows, settingsAnswer] = await Promise.all([
        getWaitlistCodes(authToken.current, scopeId ?? undefined),
        getWaitlistEntries(authToken.current, scopeId ?? undefined),
        getWaitlistAudit(authToken.current, scopeId ?? undefined),
        getWaitlistSettingsWithLastWave(authToken.current, scopeId ?? undefined),
      ]).catch(err => {
        // The unscoped probe on a stage with no current-or-next waitlist
        // answers NO_WAITLIST. Not fatal: the picker may still name a promo
        // (ended, or not started yet) to scope the reload by. Only when no
        // promo resolves either is the stage truly empty.
        if (scopeId == null && isNoWaitlistError(err)) {
          probeEmpty = true;
          return [[], [], [], { settings: null, lastWaveId: null }];
        }
        throw err;
      });
      // One GET answers both: the settings row and the effective lastWaveId.
      let settingsRow = settingsAnswer?.settings ?? null;
      let lastWaveRow = settingsAnswer?.lastWaveId ?? null;
      // The picker default follows the latest seal, then the env fallback,
      // then the newest WAITLIST promo — resolved AFTER settings so a page
      // load lands on the waitlist the service is actually running.
      const resolvedId =
        scopeId ??
        settingsRow?.shopPromoId ??
        settingsRow?.currentWave?.shopPromoId ??
        promos.find(promo => promo?.status === 'ACTIVE')?.promoId ??
        promos[0]?.promoId ??
        null;
      if (requestId !== dataRequest.current) return false;
      if (resolvedId && resolvedId !== scopeId) {
        [codeRows, entryRows, auditRows, settingsAnswer] = await Promise.all([
          getWaitlistCodes(authToken.current, resolvedId),
          getWaitlistEntries(authToken.current, resolvedId),
          getWaitlistAudit(authToken.current, resolvedId),
          getWaitlistSettingsWithLastWave(authToken.current, resolvedId),
        ]);
        settingsRow = settingsAnswer?.settings ?? null;
        lastWaveRow = settingsAnswer?.lastWaveId ?? null;
      }
      if (requestId !== dataRequest.current) return false;
      const itemsAnswer = await getWaitlistPrizeItems(authToken.current, resolvedId ?? undefined);
      if (requestId !== dataRequest.current) return false;
      setWaitlists(promos);
      if (resolvedId !== waitlistId) {
        waitlistIdRef.current = resolvedId;
        setWaitlistId(resolvedId);
      }
      setCodes(codeRows);
      setEntries(entryRows);
      setAudit(auditRows);
      setSettings(settingsRow);
      setLastWaveId(lastWaveRow ?? null);
      setPrizeItems(itemsAnswer.items);
      setPrizeItemsError(itemsAnswer.error ?? null);
      setPromoWindow(itemsAnswer.promoWindow ?? null);
      if (probeEmpty && resolvedId === null && promosFailed) {
        // Nothing to scope by AND the promo list never arrived: that is a
        // failed load, not an empty stage — historical promos may exist.
        setError(PROMO_LIST_FAILED_COPY);
        return false;
      }
      setStageEmpty(probeEmpty && resolvedId === null);
      return true;
    } catch (err) {
      console.error('Error fetching waitlist data:', err);
      if (requestId === dataRequest.current) setError(loadFailureCopy(err));
      return false;
    } finally {
      // The spinner belongs to the newest NON-quiet request. A quiet refresh
      // (mint, export, grant) that supersedes the data token must not strand
      // it: nothing else would ever clear the flag.
      if (!quiet && requestId === loadingRequest.current) setLoading(false);
    }
  };

  const resetScope = next => {
    waitlistIdRef.current = next;
    reportRequest.current += 1;
    setCodes([]);
    setEntries([]);
    setAudit([]);
    setSettings(null);
    setLastWaveId(null);
    setPrizeItems([]);
    setPrizeItemsError(null);
    setPromoWindow(null);
    setWaveReport(null);
    setNotice(null);
    setWaitlistId(next);
  };

  const onPickWaitlist = id => {
    const next = id || null;
    resetScope(next);
    navigate(withWaitlist('/waitlist', next));
    fetchAll(false, next);
  };

  // Browser Back/Forward changes ?waitlist= without remounting the page:
  // follow it, or the address names A while everything on screen is B. The
  // picker's own navigation lands here too and is a no-op (scope already
  // matches); the mount is skipped (the token effect owns the first load).
  const linkedSeen = useRef(linkedWaitlist);
  useEffect(() => {
    if (linkedSeen.current === linkedWaitlist) return;
    linkedSeen.current = linkedWaitlist;
    if (linkedWaitlist === waitlistIdRef.current) return;
    resetScope(linkedWaitlist);
    fetchAll(false, linkedWaitlist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedWaitlist]);

  const act = async (fn, scopeId = waitlistId) => {
    setError(null);
    try {
      await fn();
      if (waitlistIdRef.current !== scopeId) return false;
      // Quiet: the modal that asked (edit wave, freeze) must stay mounted
      // through the refetch. A spinner swap unmounted it before it could
      // learn the save landed but the reload did not.
      return fetchAll(true, scopeId);
    } catch (err) {
      console.error('Waitlist action failed:', err);
      if (waitlistIdRef.current === scopeId) {
        setError(err?.response?.data?.error?.message ?? 'That action failed. Nothing was changed.');
      }
      return false;
    }
  };

  const onMint = async body => {
    // The body goes to the server VERBATIM — client-side reject merging
    // lives in CreateCodesPanel, off the wire (the strict mint schema
    // refuses unknown keys; that lesson cost the first word-list mint).
    const scopeId = waitlistId;
    const result = await mintWaitlistCodes(body, authToken.current, scopeId ?? undefined);
    if (waitlistIdRef.current === scopeId) await fetchAll(true, scopeId);
    return result;
  };

  const frozenCount = entries.filter(e => e.state === 'frozen').length;

  const saveCsv = (text, filename) => {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Exports capture their scope at the click: a slow export must never hand
  // the operator waitlist A's file after the picker moved to B.
  const downloadEntriesCsv = () => {
    const scopeId = waitlistId;
    return act(async () => {
      const csv = await getWaitlistEntriesCsv(authToken.current, scopeId ?? undefined);
      if (waitlistIdRef.current !== scopeId) return;
      saveCsv(csv, 'waitlist-entries-pipeline.csv');
    }, scopeId);
  };

  // Admin's anti-sybil pipeline takes EXACTLY one column named `wallet`
  // (raw/bonanza-anti-sybil-pack README) — this flavor is ready to feed it.
  const downloadWalletsCsv = onProgress => {
    const scopeId = waitlistId;
    return act(async () => {
      const fragments = [];
      let cursor;
      let wallets = 0;
      do {
        const page = await getWaitlistAntisybilPage(
          authToken.current,
          scopeId ?? undefined,
          cursor
        );
        if (waitlistIdRef.current !== scopeId) return;
        if (page.csv.length > 0) {
          fragments.push(page.csv);
          wallets += page.csv.split('\n').filter(line => line.length > 0).length;
        }
        cursor = page.nextCursor ?? undefined;
        if (typeof onProgress === 'function') onProgress(cursor ? Math.max(wallets - 1, 0) : null);
      } while (cursor);
      saveCsv(fragments.join('\n'), 'waitlist-antisybil.csv');
    }, scopeId);
  };

  const onSaveSettings = async body => {
    if (mintOnly) return false;
    setWaveBusy(true);
    try {
      // Promo-binding v2 + Track C: an OPEN (no waveId = create) must name
      // its waitlist promo and its Cap campaign — the wave seals both at
      // creation. An EDIT never moves the seals; create never silently
      // omits rfpCampaignId (the form refuses first; this is the last line).
      const sealed =
        body.waveId || !waitlistId ? { ...body } : { ...body, shopPromoId: waitlistId };
      if (!sealed.waveId && !sealed.rfpCampaignId) {
        setError('A wave must name its Cap campaign.');
        return false;
      }
      // The saved row rides back so a CREATE can load the server-minted
      // wave (the uuid never comes from the operator).
      let saved = null;
      const ok = await act(async () => {
        saved = await saveWaitlistSettings(sealed, authToken.current, waitlistId ?? undefined);
      });
      return ok ? saved ?? true : false;
    } finally {
      setWaveBusy(false);
    }
  };

  // Mark / unmark the LAST wave. The switch writes immediately; the
  // refetch brings the server's truth back. The server accepts only the open
  // wave, or the latest closed wave while no wave is open, and returns the
  // EFFECTIVE lastWaveId (null once a newer wave makes the mark stale), so no
  // stale-mark logic lives here. An older backend refuses the intent; failures
  // show through the same error surface as other writes.
  const onSetLastWave = async (waveId, lastWave) => {
    if (mintOnly) return false;
    setLastWaveBusy(true);
    try {
      return await act(async () => {
        await setWaitlistLastWave(waveId, lastWave, authToken.current, waitlistId ?? undefined);
      });
    } finally {
      setLastWaveBusy(false);
    }
  };

  const onCloseWave = async waveId => {
    if (mintOnly) return;
    const scopeId = waitlistId;
    const requestId = ++reportRequest.current;
    setWaveBusy(true);
    setError(null);
    setNotice(null);
    let closeAccepted = false;
    let closedStateLoaded = false;
    try {
      await closeWaitlistWave(waveId, authToken.current, scopeId ?? undefined);
      closeAccepted = true;
      if (requestId !== reportRequest.current) return;
      if (!(await fetchAll(true, scopeId))) throw new Error('Closed wave refresh failed.');
      closedStateLoaded = true;
      if (requestId !== reportRequest.current) return;
      const report = await loadCompletedWaveReport(
        () => getWaitlistWaveReport(waveId, authToken.current, scopeId ?? undefined),
        waveId
      );
      if (requestId === reportRequest.current) setWaveReport(report);
      if (requestId === reportRequest.current && !(await fetchAll(true, scopeId))) {
        throw new Error('Closed wave refresh failed.');
      }
    } catch (err) {
      console.error('Wave close failed:', err);
      if (requestId !== reportRequest.current) return;
      if (closeAccepted) {
        // Slow report and dead close job look identical to the poll — the
        // wave itself tells them apart: still open after the ~20s report
        // wait = the job failed, and that must read RED, not patient amber.
        // Only after the full poll, though: seconds after accept the job
        // legitimately has not run yet.
        let stillOpen = false;
        let latest = null;
        if (closedStateLoaded) {
          try {
            latest = await getWaitlistSettings(authToken.current, scopeId ?? undefined);
            stillOpen = latest?.currentWave?.waveId === waveId;
          } catch {
            // Unknowable — keep the calm notice rather than a false alarm.
            latest = null;
          }
        }
        if (requestId !== reportRequest.current) return;
        if (stillOpen) {
          setError(
            'The close was accepted but the wave is STILL OPEN: the close job failed in the background. Retry the close. If it repeats, check the close queue DLQ.'
          );
        } else {
          // The probe may have just SEEN the close land (the worker cleared
          // currentWave after our refresh ran). Publish it: a full refresh
          // brings the audit row too, else at least the settings. Otherwise
          // the page keeps calling a closed wave open — Report tab hidden,
          // live levers back on.
          if (latest && !(await fetchAll(true, scopeId))) setSettings(latest);
          if (requestId !== reportRequest.current) return;
          setNotice(
            closedStateLoaded
              ? 'The wave close was accepted, but its report is still processing. Open the Report tab to retry.'
              : 'The wave close was accepted. Refresh the waitlist to load its report.'
          );
        }
      } else {
        setError(err?.response?.data?.error?.message ?? 'The wave did not close.');
      }
    } finally {
      setWaveBusy(false);
    }
  };

  const onLoadReport = useCallback(
    async waveId => {
      const scopeId = waitlistId;
      const requestId = ++reportRequest.current;
      // A placeholder that already NAMES the wave, never null: the workspace
      // re-requests any report whose waveId is not its own, so a null here
      // fired a second five-attempt poll on every retry and wave change.
      setWaveReport({ report: { waveId }, loading: true });
      try {
        const report = await loadCompletedWaveReport(
          () => getWaitlistWaveReport(waveId, authToken.current, scopeId ?? undefined),
          waveId,
          { attempts: 5 }
        );
        if (requestId !== reportRequest.current) return;
        setWaveReport(report);
        setNotice(null); // the report landed: no close warning outlives it
        // The grant row lands AFTER the close (the close-and-grant chain), so
        // an audit read before the poll can still call the prizes "not
        // dispatched" and send the operator to grant by hand. Refresh the
        // scope now that the report is in.
        if (waitlistIdRef.current === scopeId) await fetchAll(true, scopeId);
      } catch (err) {
        console.error('Wave report load failed:', err);
        if (requestId === reportRequest.current) {
          setWaveReport({ report: { waveId }, error: true });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [waitlistId]
  );

  // Promo names for the seal chips: promoId -> the human name.
  const promoNameFor = promoId =>
    waitlists.find(promo => promo.promoId === promoId)?.promoName ?? promoId ?? null;
  const selectedWaitlist = waitlists.find(promo => promo.promoId === waitlistId) ?? null;

  // Names, never uuids, anywhere a human reads (Gwen 2026-08-19).
  const closedWaves = wavesFromAudit(audit);
  const waveNameFor = waveId => {
    if (settings?.currentWave?.waveId === waveId) return settings.currentWave.name;
    return closedWaves.find(wave => wave.waveId === waveId)?.name ?? waveId ?? '—';
  };
  const entriesNamed = entries.map(entry => ({ ...entry, waveName: waveNameFor(entry.waveId) }));
  const codesNamed = codes.map(code => ({
    ...code,
    waveName: code.waveId ? waveNameFor(code.waveId) : null,
  }));

  // Breadcrumb from the URL: Waves > the wave's NAME > Invite Codes
  const waveMatch = location.pathname.match(/\/waitlist\/waves\/([^/]+)(?:\/([^/]+))?/);
  const crumbWave = waveMatch?.[1] ?? null;
  const crumbTab = waveMatch?.[2] && TAB_NAMES[waveMatch[2]] ? TAB_NAMES[waveMatch[2]] : null;

  const waveProps = {
    promoNameFor,
    waitlistId,
    settings,
    items: prizeItems,
    itemsError: prizeItemsError,
    promoWindow,
    audit,
    entries: entriesNamed,
    codes: codesNamed,
    report: waveReport,
    onLoadReport,
    onSaveSettings,
    onCloseWave,
    lastWaveId,
    onSetLastWave,
    lastWaveBusy,
    onMint,
    onSetEntryState: async change => {
      if (!canFreeze) return false;
      const scopeId = waitlistId;
      setError(null);
      try {
        await setWaitlistEntryState(change, authToken.current, scopeId ?? undefined);
        // The lever LANDED (the ADJ audit row is written): say so even when
        // the follow-up read fails, or the reason modal stays open and a
        // retry writes the same permanent adjustment twice. The page error
        // reports the reload on its own.
        if (waitlistIdRef.current === scopeId) await fetchAll(true, scopeId);
        return true;
      } catch (err) {
        console.error('Entry state change failed:', err);
        if (waitlistIdRef.current === scopeId) {
          setError(
            err?.response?.data?.error?.message ?? 'That action failed. Nothing was changed.'
          );
        }
        return false;
      }
    },
    onGrantJelly: async ({ batchId, bands }) => {
      if (mintOnly) return null;
      const scopeId = waitlistId;
      setError(null);
      try {
        const result = await grantWaitlistJelly(
          { batchId, bands },
          authToken.current,
          scopeId ?? undefined
        );
        // The mutation LANDED: hand its answer back even when the follow-up
        // read fails (the page error says the reload failed). A null here
        // made the modal call the batch lost and re-send wallets that were
        // already granted or frozen.
        if (waitlistIdRef.current === scopeId) await fetchAll(true, scopeId);
        return result;
      } catch (err) {
        console.error('Grant Jelly failed:', err);
        if (waitlistIdRef.current === scopeId) {
          setError(
            err?.response?.data?.error?.message ??
              'The grant did not run. Nothing was dropped. Retry with the same batch.'
          );
        }
        return null;
      }
    },
    onBulkFreeze: async ({ walletIds, reason }) => {
      if (!canFreeze) return null;
      const scopeId = waitlistId;
      setError(null);
      try {
        const result = await bulkWaitlistEntryState(
          { walletIds, state: 'frozen', reason },
          authToken.current,
          scopeId ?? undefined
        );
        // The mutation LANDED: hand its answer back even when the follow-up
        // read fails (the page error says the reload failed). A null here
        // made the modal call the batch lost and re-send wallets that were
        // already granted or frozen.
        if (waitlistIdRef.current === scopeId) await fetchAll(true, scopeId);
        return result;
      } catch (err) {
        console.error('Bulk freeze failed:', err);
        if (waitlistIdRef.current === scopeId) {
          setError(
            err?.response?.data?.error?.message ??
              'Bulk freeze interrupted. Some wallets may have changed. Refresh before retrying.'
          );
        }
        return null;
      }
    },
    onExportCsv: downloadEntriesCsv,
    onExportWalletsCsv: downloadWalletsCsv,
    saving: waveBusy,
    closing: waveBusy,
    error,
    mintOnly,
    canFreeze,
  };

  return (
    <div className="container">
      <Form.Group className="d-flex align-items-center gap-2 my-3" controlId="waitlist-picker">
        <Form.Label className="mb-0 text-nowrap fw-semibold">Waitlist</Form.Label>
        <Form.Select
          value={waitlistId ?? ''}
          onChange={e => onPickWaitlist(e.target.value)}
          aria-label="waitlist"
          style={{ maxWidth: '28rem' }}
          disabled={loading || waveBusy}
        >
          {waitlists.length === 0 && (
            <option value="">no WAITLIST promos yet: create one in Promos</option>
          )}
          {waitlistId !== null && !waitlists.some(promo => promo.promoId === waitlistId) && (
            <option value={waitlistId}>{waitlistId}</option>
          )}
          {waitlists.map(promo => (
            <option key={promo.promoId} value={promo.promoId}>
              {promo.promoName ?? promo.promoId}
            </option>
          ))}
        </Form.Select>
        {selectedWaitlist?.startTime && selectedWaitlist?.endTime && (
          <span className="text-muted small text-nowrap">
            {formatUtcWindow(selectedWaitlist.startTime, selectedWaitlist.endTime)}
          </span>
        )}
      </Form.Group>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: withWaitlist('/waitlist', waitlistId) }}>
          Waves
        </Breadcrumb.Item>
        {crumbWave && !crumbTab && (
          <Breadcrumb.Item active>{waveNameFor(crumbWave)}</Breadcrumb.Item>
        )}
        {crumbWave && crumbTab && (
          <>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: wavePath(waitlistId, crumbWave) }}>
              {waveNameFor(crumbWave)}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{crumbTab}</Breadcrumb.Item>
          </>
        )}
      </Breadcrumb>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center border-bottom">
        <div>
          <div className="game-micro">launch waitlist promo</div>
          <h4 className="mb-0">Waitlist</h4>
        </div>
        <div className="text-muted small">
          {entries.length} entries · {frozenCount} frozen
        </div>
      </div>
      {error && (
        <Alert variant="danger" className="my-3">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="warning" className="my-3">
          {notice}
        </Alert>
      )}
      {stageEmpty && !error && (
        <Alert variant="secondary" className="my-3">
          {NO_WAITLIST_COPY}
        </Alert>
      )}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Routes>
          <Route
            index
            element={
              <WavesList
                promoNameFor={promoNameFor}
                waitlistId={waitlistId}
                settings={settings}
                items={prizeItems}
                itemsError={prizeItemsError}
                promoWindow={promoWindow}
                audit={audit}
                entries={entriesNamed}
                codes={codesNamed}
                onSaveSettings={onSaveSettings}
                saving={waveBusy}
                error={error}
                lastWaveId={lastWaveId}
                waitlists={waitlists}
                mintOnly={mintOnly}
              />
            }
          />
          <Route path="waves/:waveId" element={<WaveRoute {...waveProps} />} />
          <Route path="waves/:waveId/:tab" element={<WaveRoute {...waveProps} />} />
        </Routes>
      )}
    </div>
  );
};

export default Waitlist;
